/**
 * <footer-menu>
 *
 * Turns the footer's menu columns into plus-sign accordions below 768px, and
 * back into plain columns above it.
 *
 * WHY THIS ISN'T assets/site-header.js: that file's accordion handler lives
 * inside <site-header> and looks its panels up with this.querySelector, so it
 * only ever reaches markup inside the header. The data-attribute contract is
 * deliberately identical to it, though, so the two read the same:
 *
 *   [data-accordion-toggle]     button, carries aria-expanded + aria-controls
 *   [data-accordion-panel]      the panel, id matches the toggle's aria-controls
 *   [data-accordion-icon-plus]  \ swapped on open/close
 *   [data-accordion-icon-minus] /
 *   [data-column-label]         the non-interactive title used above 768px
 *
 * EVERYTHING IS TOGGLED WITH THE `hidden` ATTRIBUTE, never a display utility.
 * Tailwind's preflight sets [hidden] to display:none with !important, so it
 * cannot lose a specificity race to a class on the same element — and the hidden
 * element leaves the accessibility tree, which is the whole point of hiding the
 * title that does not apply at the current width.
 *
 * THE MARKUP SHIPS IN THE DESKTOP STATE: label visible, button hidden, panel
 * open. So with JavaScript off, or before this file has run, the footer is fully
 * readable and every link works. This script only ever ADDS the collapse.
 */
if (!customElements.get("footer-menu")) {
  customElements.define(
    "footer-menu",
    class FooterMenu extends HTMLElement {
      /* In step with the design system's `md` breakpoint — 48rem in
         assets/css-tailwind.css. If that moves, this moves with it. */
      static DESKTOP_BREAKPOINT = 768;

      connectedCallback() {
        this.columns = Array.from(this.querySelectorAll("[data-accordion-toggle]"))
          .map((toggle) => {
            const id = toggle.getAttribute("aria-controls");
            const panel = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
            const column = toggle.closest("li");

            return {
              toggle,
              panel,
              label: column ? column.querySelector("[data-column-label]") : null,
            };
          })
          .filter((column) => column.panel);

        if (!this.columns.length) return;

        this.desktop = window.matchMedia(
          `(min-width: ${FooterMenu.DESKTOP_BREAKPOINT}px)`,
        );

        this.handleClick = this.handleClick.bind(this);
        this.handleBreakpoint = this.handleBreakpoint.bind(this);

        /* One delegated listener rather than one per toggle, so there is a
           single thing to remove on disconnect. */
        this.addEventListener("click", this.handleClick);
        this.desktop.addEventListener("change", this.handleBreakpoint);

        this.applyMode();
      }

      disconnectedCallback() {
        if (this.handleClick) this.removeEventListener("click", this.handleClick);
        if (this.desktop && this.handleBreakpoint) {
          this.desktop.removeEventListener("change", this.handleBreakpoint);
        }
      }

      /* ------------------------------------------------------------------- */

      handleClick(event) {
        const toggle = event.target.closest("[data-accordion-toggle]");
        if (!toggle || !this.contains(toggle)) return;

        const column = this.columns.find((candidate) => candidate.toggle === toggle);
        if (!column) return;

        this.setOpen(column, toggle.getAttribute("aria-expanded") !== "true");
      }

      handleBreakpoint() {
        this.applyMode();
      }

      /**
       * Swaps which title is showing and resets the panels.
       *
       * Entering accordion mode always collapses everything rather than trying
       * to preserve whatever was open before — a footer that reopens a panel the
       * visitor closed three resizes ago is stranger than one that just starts
       * shut.
       */
      applyMode() {
        const isDesktop = this.desktop.matches;

        this.columns.forEach((column) => {
          if (column.label) column.label.hidden = !isDesktop;
          column.toggle.hidden = isDesktop;

          this.setOpen(column, isDesktop);
        });
      }

      setOpen(column, isOpen) {
        column.toggle.setAttribute("aria-expanded", String(isOpen));
        column.panel.hidden = !isOpen;

        const plus = column.toggle.querySelector("[data-accordion-icon-plus]");
        const minus = column.toggle.querySelector("[data-accordion-icon-minus]");

        if (plus) plus.hidden = isOpen;
        if (minus) minus.hidden = !isOpen;
      }
    },
  );
}
